import { auth } from '@/lib/firebase';
import { useState, useEffect, useMemo } from 'react';
import { db } from '@/lib/firebase';
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { useAuth } from '@/contexts/AuthContext';

export interface StaffDeal {
  stage: string;
  id: string;
  title: string;
  assignedToStaffId: string | null;
  status: string;
  ndaStatus: 'sent_to_buyer' | 'signed' | 'declined' | 'not_required' | 'pending';
  ndaSentAt: Date | null;
  ndaPdfUrl: string | null;
  priority: 'high' | 'normal' | 'low';
  submittedAt: Date;
  lastUpdate: Date;
  role: 'Seller' | 'Broker' | 'BYOD' | 'Buyer Elite';
  industry?: string;
  askingPrice?: number;
  source?: 'deals' | 'dealSubmissions';
}

export type ViewType = 'todo' | 'triage' | 'all' | 'pipeline';

// ─────────────────────────────────────────────────────────────────────────
// CORRECTIF (5 août 2026) : les documents écrits par le webhook Stripe
// NestJS (payments.service.ts / DealApprovalService) stockent leurs
// timestamps en chaînes ISO (`new Date().toISOString()`), alors que ceux
// écrits via les Cloud Functions Firebase (cim-generation.service.ts, etc.)
// utilisent de vrais Timestamp Firestore. `data.createdAt?.toDate?.()`
// échoue silencieusement sur une chaîne (pas de méthode .toDate), et sur
// un champ absent — dans les deux cas le code retombait sur `new Date()`,
// donc l'heure du RENDU de la page plutôt que la vraie date de soumission.
// C'est pourquoi tous les deals affichaient "less than a minute ago" à
// chaque ouverture du dashboard. `toJsDate()` gère maintenant les deux
// formats, et on regarde tous les noms de champs réellement utilisés
// (createdAt, submittedAt, updatedAt, updated_at) avant d'abandonner.
// ─────────────────────────────────────────────────────────────────────────

function toJsDate(value: any): Date | null {
  if (!value) return null;
  // Vrai Timestamp Firestore
  if (typeof value?.toDate === 'function') return value.toDate();
  // Chaîne ISO (webhook NestJS) ou nombre (ms epoch)
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  // Objet Timestamp brut sérialisé { seconds, nanoseconds }
  if (typeof value?.seconds === 'number') {
    return new Date(value.seconds * 1000);
  }
  return null;
}

// Priorité selon l'ancienneté
const determinePriority = (createdAt: any): StaffDeal['priority'] => {
  const date = toJsDate(createdAt) || new Date();
  const days = Math.floor((Date.now() - date.getTime()) / 86400000);
  if (days > 7) return 'high';
  if (days > 3) return 'normal';
  return 'low';
};

// Mapper un document Firestore en StaffDeal
const mapDoc = (docSnap: any, source: 'deals' | 'dealSubmissions'): StaffDeal => {
  const data = docSnap.data();

  // Ordre de préférence : le vrai moment de soumission d'abord, peu importe
  // le nom de champ ou le format utilisé par le chemin d'écriture concerné.
  const submitted =
    toJsDate(data.createdAt) ||
    toJsDate(data.submittedAt) ||
    toJsDate(data.submitted_at) ||
    new Date();

  const updated =
    toJsDate(data.updatedAt) ||
    toJsDate(data.updated_at) ||
    submitted;

  return {
    id: docSnap.id,
    title: data.businessName || data.title || data.companyName || 'Untitled Deal',
    assignedToStaffId: data.assignedStaffId || data.assignedToStaffId || null,
    status: data.status || (source === 'dealSubmissions' ? 'submitted' : 'draft'),
    stage: data.current_stage || data.stage || data.status || 'submission',
    submittedAt: submitted,
    lastUpdate: updated,
    priority: determinePriority(data.createdAt || data.submittedAt || data.submitted_at),
    role: data.submitterRole || (data.brokerId ? 'Broker' : 'Seller'),
    askingPrice: data.askingPrice ? Number(data.askingPrice) : undefined,
    industry: data.industry,
    ndaStatus: data.ndaStatus || 'pending',
    ndaSentAt: toJsDate(data.ndaSentAt),
    ndaPdfUrl: data.ndaPdfUrl || null,
    source,
  };
};

// 'under_review' ajouté : c'est le statut écrit par deals.service.ts
// (submitDeal -> mirror vers la collection 'deals') pour toute nouvelle
// soumission. Sans lui, ces deals n'apparaissaient jamais en Triage — ils
// atterrissaient directement (et silencieusement) dans "Active Deals" sans
// jamais être assignés à personne.
const TRIAGE_STATUSES = ['submitted', 'pending_review', 'pending-review', 'pending', 'draft', 'new', 'awaiting_review', 'under_review'];
const ACTIVE_STATUSES = ['active', 'under_review', 'awaiting_seller_approval', 'cim_review', 'review_in_progress'];

export function useStaffDeals(view: ViewType) {
  const { user } = useAuth();
  const [allDeals, setAllDeals] = useState<StaffDeal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((firebaseUser) => {
      if (!firebaseUser) {
        setLoading(false);
        return;
      }

      console.log('[useStaffDeals] user ready:', firebaseUser.uid);
      setLoading(true);

      const run = async () => {
        try {
          const [dealsSnap, subsSnap] = await Promise.allSettled([
            getDocs(collection(db, 'deals')),
            getDocs(collection(db, 'dealSubmissions')),
          ]);

          const merged = new Map<string, StaffDeal>();

          // dealSubmissions en premier (priorité inférieure)
          if (subsSnap.status === 'fulfilled') {
            subsSnap.value.docs.forEach(d =>
              merged.set(d.id, mapDoc(d, 'dealSubmissions'))
            );
          }
          // deals écrase dealSubmissions (source de vérité)
          if (dealsSnap.status === 'fulfilled') {
            dealsSnap.value.docs.forEach(d =>
              merged.set(d.id, mapDoc(d, 'deals'))
            );
          }

          const all = Array.from(merged.values()).sort(
            (a, b) => b.submittedAt.getTime() - a.submittedAt.getTime()
          );

          console.log('[useStaffDeals] deals loaded:', all.length);
          setAllDeals(all);
        } catch (err: any) {
          console.error('[useStaffDeals] error:', err);
          setError(err?.message || String(err));
        } finally {
          setLoading(false);
        }
      };

      run();
    });

    return () => unsubscribe();
  }, []); // s'exécute une seule fois

  const staffId = auth.currentUser?.uid || (user as any)?.uid || null;

  // Filtrage par vue
  const deals = useMemo(() => {
    switch (view) {
      case 'todo':
        return allDeals.filter(d => d.assignedToStaffId === staffId);
      case 'triage':
        return allDeals.filter(d =>
          !d.assignedToStaffId && TRIAGE_STATUSES.includes(d.status)
        );
      case 'all':
        return allDeals;
      default:
        return allDeals;
    }
  }, [allDeals, view, staffId]);

  // KPIs sur le dataset complet
  const kpis = useMemo(() => ({
    assignedDeals: allDeals.filter(d => d.assignedToStaffId === staffId).length,
    todayDeals: allDeals.filter(d => d.assignedToStaffId === staffId && d.priority === 'high').length,
    cimToReview: allDeals.filter(d => d.status === 'cim_ready_for_internal_review' || d.status === 'awaiting_review').length,
    waitingClient: allDeals.filter(d => d.status === 'pending_client_info' || d.status === 'awaiting_seller_approval').length,
    triageCount: allDeals.filter(d => !d.assignedToStaffId && TRIAGE_STATUSES.includes(d.status)).length,
    activeCount: allDeals.filter(d => ACTIVE_STATUSES.includes(d.status)).length,
    totalActive: allDeals.length,
    ndaPending: allDeals.filter(d => d.ndaStatus === 'sent_to_buyer').length,
    ndaSigned: allDeals.filter(d => d.ndaStatus === 'signed').length,
  }), [allDeals, staffId]);

  // Stats pipeline
  const pipelineStats = useMemo(() => {
    const categories = ['Draft', 'Pending', 'In Review', 'Listed', 'Sold'];
    return categories.map(cat => ({
      label: cat,
      count: allDeals.filter(d =>
        d.status?.toLowerCase().includes(cat.toLowerCase())
      ).length,
    }));
  }, [allDeals]);

  // Prendre en charge un dossier
  const takeDeal = async (dealId: string) => {
    try {
      const deal = allDeals.find(d => d.id === dealId);
      const colName = deal?.source === 'dealSubmissions' ? 'dealSubmissions' : 'deals';
      await updateDoc(doc(db, colName, dealId), {
        assignedStaffId: staffId,
        status: 'review_in_progress',
        updatedAt: Timestamp.now(),
      });
      // Mise à jour locale immédiate sans reload
      setAllDeals(prev =>
        prev.map(d =>
          d.id === dealId
            ? { ...d, assignedToStaffId: staffId, status: 'review_in_progress' }
            : d
        )
      );
    } catch (err) {
      console.error('[useStaffDeals] takeDeal error:', err);
    }
  };

  return {
    deals,
    allDeals,
    loading,
    error,
    kpis,
    pipelineStats,
    takeDeal,
    staffId,
    refetch: () => {},
  };
}